export const TEAM_SIZES = ['1-10', '11-50', '51-200', '200+'] as const

export type TeamSize = typeof TEAM_SIZES[number]

export const TEAM_SIZE_LABELS: Record<TeamSize, string> = {
    '1-10': "1–10",
    '11-50': "11–50",
    '51-200': "51–200",
    '200+': "200+"
}

export interface DemoRequestInput {
    name: string
    business: string
    phone: string
    email: string
    teamSize: string
    message: string
}

export type DemoRequestField = keyof DemoRequestInput

export type DemoRequestErrors = Partial<Record<DemoRequestField, string>>

export const DEMO_REQUEST_FIELDS: Array<DemoRequestField> = ['name', 'business', 'phone', 'email', 'teamSize', 'message']

export const DEMO_REQUEST_LIMITS = {
    name: 100,
    business: 120,
    phone: 20,
    email: 160,
    message: 1000
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_PATTERN = /^\+?[\d\s()-]+$/

export const isTeamSize = (value: string): value is TeamSize => (TEAM_SIZES as ReadonlyArray<string>).includes(value)

export const normalizeDemoRequest = (raw: unknown): DemoRequestInput => {
    const source = typeof raw === 'object' && raw !== null ? raw as Record<string, unknown> : {}
    const read = (key: DemoRequestField): string => {
        const value = source[key]

        return typeof value === 'string' ? value.trim() : ''
    }

    return {
        name: read('name'),
        business: read('business'),
        phone: read('phone'),
        email: read('email'),
        teamSize: read('teamSize'),
        message: read('message')
    }
}

export const validateDemoRequest = (input: DemoRequestInput): DemoRequestErrors => {
    const errors: DemoRequestErrors = {}
    const phoneDigits = input.phone.replace(/\D/g, '')

    if (input.name.length < 2) errors.name = "Please enter your name."
    else if (input.name.length > DEMO_REQUEST_LIMITS.name) errors.name = "Please keep your name under 100 characters."

    if (input.business.length < 2) errors.business = "Please enter your business name."
    else if (input.business.length > DEMO_REQUEST_LIMITS.business) errors.business = "Please keep the business name under 120 characters."

    if (!input.phone) errors.phone = "Please enter your mobile number."
    else if (!PHONE_PATTERN.test(input.phone) || phoneDigits.length < 7 || phoneDigits.length > 15 || input.phone.length > DEMO_REQUEST_LIMITS.phone) errors.phone = "Please enter a valid mobile number."

    if (input.email && (!EMAIL_PATTERN.test(input.email) || input.email.length > DEMO_REQUEST_LIMITS.email)) errors.email = "Please enter a valid email address, or leave it empty."

    if (input.teamSize && !isTeamSize(input.teamSize)) errors.teamSize = "Please choose one of the team sizes."

    if (input.message.length > DEMO_REQUEST_LIMITS.message) errors.message = "Please keep your message under 1,000 characters."

    return errors
}
